import type { LatLngBoundsLiteral } from 'leaflet';

// ============================================================
// Images fixes du terrain, en attendant les tuiles RecubeDynmap
// ============================================================
// Un niveau sans tileUrl peut avoir une image fixe de son terrain : posée en L.imageOverlay sous la
// grille des chunks (useWorldMap), chargée seulement quand une carte de ce niveau s'affiche. Avec
// une tileUrl, les tuiles restent prioritaires et l'image n'est jamais demandée.
// Les images sont dans public/worlds/ (servies telles quelles, préfixe import.meta.env.BASE_URL).
// Bornes en BLOCS, bords de l'image (maxX et maxZ exclus) : même monde et mêmes coordonnées que le
// jeu, calage vérifié à ±2 blocs sur les chunks peints importés de NG Java.
// ============================================================

export interface StaticBackground {
  url: string;
  /** Emprise en blocs : bords gauche, droit, haut et bas de l'image. */
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  /** Taille de l'image, en pixels (échelle : (maxX - minX) / width blocs par pixel). */
  width: number;
  height: number;
  label: string;
  /** Monde d'où vient le rendu (textes de la note, de l'accueil et des niveaux). */
  origin: string;
}

const BASE = import.meta.env.BASE_URL;

/** Nom du niveau → image fixe de son terrain. */
export const STATIC_BACKGROUNDS: Readonly<Record<string, StaticBackground>> = {
  // Dynmap NG Java, monde « world », map flat au zoom 4, réduit à 6 blocs par pixel (3,97 Mo).
  overworld: {
    url: `${BASE}worlds/ng-overworld-bg.webp`,
    minX: -12096,
    maxX: 12096,
    minZ: -9056,
    maxZ: 8032,
    width: 4032,
    height: 2848,
    label: 'Terrain NG Java (image fixe)',
    origin: 'NG Java',
  },
};

/** Image fixe à afficher pour un niveau : seulement s'il n'a pas de tuiles. */
export function staticBackgroundFor(level: { name: string; tileUrl: string | null } | null | undefined): StaticBackground | null {
  if (!level || level.tileUrl) return null;
  return Object.prototype.hasOwnProperty.call(STATIC_BACKGROUNDS, level.name) ? STATIC_BACKGROUNDS[level.name] : null;
}

/** Emprise Leaflet (L.CRS.Simple, lat = -z/16, lng = x/16) : sud-ouest, nord-est. */
export function staticBackgroundBounds(bg: StaticBackground): LatLngBoundsLiteral {
  return [[-bg.maxZ / 16, bg.minX / 16], [-bg.minZ / 16, bg.maxX / 16]];
}

/** Pixels écran occupés par un pixel de l'image au zoom `zoom` (2^zoom px par chunk). */
export function screenPxPerImagePx(bg: StaticBackground, zoom: number): number {
  return (Math.pow(2, zoom) * (bg.maxX - bg.minX)) / 16 / bg.width;
}
